import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircleFlag } from 'react-circle-flags';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

const MAX_VISIBLE = 1;

export type ShippingListItem = {
  id: string;
  label: string;
  countryCode?: string;
};

type ShippingListCellProps = {
  items: ShippingListItem[];
  modalTitle: string;
  allLabel?: string;
  isAll?: boolean;
  getItemHref?: (item: ShippingListItem) => string | undefined;
  showCountryFlags?: boolean;
};

const chipClassName =
  'inline-flex max-w-[140px] items-center gap-1 truncate rounded-md bg-zinc-800/60 px-2 py-1 text-[11px] font-medium text-zinc-300 shrink-0';

const chipLinkClassName =
  'font-medium text-primary hover:bg-zinc-700/80 hover:text-primary transition-all duration-150 active:scale-95';

const overflowButtonClassName =
  'cursor-pointer rounded-md bg-zinc-800 px-2 py-1 text-[11px] font-semibold text-zinc-300 hover:bg-zinc-700 hover:text-white transition-all duration-150 active:scale-95 shrink-0';

function ShippingListChip({
  item,
  href,
  showCountryFlag,
}: {
  item: ShippingListItem;
  href?: string;
  showCountryFlag?: boolean;
}) {
  const label = item.label || item.id;
  const flagCode = item.countryCode?.toLowerCase();

  const content = (
    <>
      {showCountryFlag && flagCode ? (
        <span className="inline-flex h-3.5 w-3.5 shrink-0 overflow-hidden rounded-full">
          <CircleFlag countryCode={flagCode} height={14} width={14} />
        </span>
      ) : null}
      <span className="truncate">{label}</span>
    </>
  );

  if (href) {
    return (
      <Link
        to={href}
        className={`${chipClassName} ${chipLinkClassName}`}
        title={label}
      >
        {content}
      </Link>
    );
  }

  return (
    <span className={chipClassName} title={label}>
      {content}
    </span>
  );
}

function ShippingListPopoverRow({
  item,
  href,
  showCountryFlag,
}: {
  item: ShippingListItem;
  href?: string;
  showCountryFlag?: boolean;
}) {
  const label = item.label || item.id;
  const flagCode = item.countryCode?.toLowerCase();

  const inner = (
    <>
      {showCountryFlag && flagCode ? (
        <span className="inline-flex h-4 w-4 shrink-0 overflow-hidden rounded-full">
          <CircleFlag countryCode={flagCode} height={16} width={16} />
        </span>
      ) : null}
      <span className="text-xs text-zinc-300 truncate">{label}</span>
    </>
  );

  if (href) {
    return (
      <Link
        to={href}
        className="flex items-center gap-2.5 px-3 py-1.5 rounded-md hover:bg-zinc-800/60 transition-colors text-primary hover:text-primary/90"
      >
        {inner}
      </Link>
    );
  }

  return (
    <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-md hover:bg-zinc-800/60 transition-colors">
      {inner}
    </div>
  );
}

export default function ShippingListCell({
  items,
  modalTitle,
  allLabel,
  isAll = false,
  getItemHref,
  showCountryFlags = false,
}: ShippingListCellProps) {
  const { t } = useTranslation('common');
  const resolvedAllLabel = allLabel ?? t('all');

  if (isAll) {
    return <span className="text-sm">{resolvedAllLabel}</span>;
  }

  if (!items.length) {
    return <span className="text-sm text-muted">—</span>;
  }

  const visibleItems = items.slice(0, MAX_VISIBLE);
  const hiddenItems = items.slice(MAX_VISIBLE);
  const hiddenCount = hiddenItems.length;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {visibleItems.map((item) => (
        <ShippingListChip
          key={item.id}
          item={item}
          href={getItemHref?.(item)}
          showCountryFlag={showCountryFlags}
        />
      ))}

      {hiddenCount > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className={overflowButtonClassName}>
              +{hiddenCount}
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="bottom"
            align="start"
            className="w-[320px] max-h-[380px] overflow-y-auto p-0 bg-zinc-900 border-zinc-700/80 shadow-2xl rounded-xl"
          >
            <div className="px-4 py-3 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10">
              <h4 className="text-sm font-semibold text-white">{modalTitle}</h4>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                {items.length} {t('configured')}
              </p>
            </div>
            <div className="p-2 space-y-0.5">
              {items.map((item) => (
                <ShippingListPopoverRow
                  key={item.id}
                  item={item}
                  href={getItemHref?.(item)}
                  showCountryFlag={showCountryFlags}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
